import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authFetch } from '@/lib/auth-fetch';
import { getApiBaseUrl } from '@/lib/api-base-url';
import { getAccessToken } from '@/features/auth/lib/session';
import { beginImpersonation, endImpersonation } from '@/features/auth/lib/impersonation-session';

export class ImpersonationApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ImpersonationApiError';
    this.status = status;
  }
}

async function readError(response: Response): Promise<ImpersonationApiError> {
  let message = `Request failed (${String(response.status)})`;
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === 'string') message = body.message;
  } catch {
    // body wasn't JSON
  }
  return new ImpersonationApiError(response.status, message);
}

async function startImpersonation(targetId: string): Promise<null> {
  const response = await authFetch(`${getApiBaseUrl()}/admin/users/${targetId}/impersonate`, {
    method: 'POST',
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { accessToken?: unknown };
  if (typeof body.accessToken !== 'string') {
    throw new ImpersonationApiError(500, 'Malformed impersonation response.');
  }
  // Read the admin token after the call so a mid-request refresh is preserved, not the stale one.
  const adminToken = getAccessToken();
  if (adminToken === null) {
    throw new ImpersonationApiError(401, 'Your session expired. Sign in again.');
  }
  beginImpersonation({ overlayToken: body.accessToken, adminToken, targetId });
  return null;
}

async function stopImpersonation(): Promise<null> {
  // Best-effort server audit while the overlay token is still active; the local restore matters most.
  try {
    await authFetch(`${getApiBaseUrl()}/impersonation/stop`, { method: 'POST' });
  } catch {
    // ignore — fall through to the local restore
  }
  endImpersonation();
  return null;
}

export function useStartImpersonation() {
  const client = useQueryClient();
  return useMutation<null, ImpersonationApiError, string>({
    mutationFn: startImpersonation,
    onSuccess: () => {
      client.clear();
    },
  });
}

export function useStopImpersonation() {
  const client = useQueryClient();
  return useMutation<null, ImpersonationApiError>({
    mutationFn: () => stopImpersonation(),
    onSuccess: () => {
      client.clear();
    },
  });
}
